<!-- src\components\popup\PopupManager.vue -->
<template>
	<Teleport to="body">
		<div
			v-for="popup in popups"
			:key="popup.id"
			class="popup"
			:class="{ popup_show: popup.visible }"
			:aria-hidden="!popup.visible"
			:inert="!popup.visible"
			@click.self="closePopup(popup.id)"
		>
			<div class="popup__wrapper">
				<div class="popup__content">
					<button
						data-close
						type="button"
						class="popup__close"
						@click="closePopup(popup.id)"
					>
						<svg viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M1 1L37 37M37 1L1 37"
								stroke="currentColor"
								stroke-width="1.5"
							/>
						</svg>
					</button>

					<div class="popup__body">
						<component :is="popup.component" v-bind="popup.props" />
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup>
import { popupRegistry } from '@/components/popup/popupRegistry'
import { onMounted, onUnmounted, ref, nextTick, markRaw } from 'vue'
import { bodyLock, bodyUnlock } from '@/utils/lockScroll'

const popups = ref([])

function openPopup(id, component = null, props = {}) {
	const existing = popups.value.find((p) => p.id === id)

	if (!existing) {
		const loadComponent = component
			? Promise.resolve({ default: component })
			: popupRegistry[id]
				? popupRegistry[id]()
				: Promise.reject(`Компонент с id "${id}" не найден в popupRegistry`)

		loadComponent
			.then(({ default: Comp }) => {
				markRaw(Comp)

				popups.value.push({
					id,
					component: markRaw(Comp),
					props,
					visible: false,
					closingTimeout: null,
				})

				nextTick(() => {
					requestAnimationFrame(() => {
						const popup = popups.value.find((p) => p.id === id)
						if (popup) {
							popup.visible = true
							updateBodyLock()
						}
					})
				})
			})
			.catch((err) => {
				console.warn(`Ошибка загрузки попапа "${id}":`, err)
			})
	} else {
		if (existing.closingTimeout) {
			clearTimeout(existing.closingTimeout)
			existing.closingTimeout = null
		}
		if (!existing.visible) {
			existing.visible = true
			updateBodyLock()
		}
	}
}

function closePopup(id) {
	const popup = popups.value.find((p) => p.id === id)
	if (popup && popup.visible) {
		nextTick(() => {
			requestAnimationFrame(() => {
				const popupElement =
					document.querySelector(`.popup[key="${id}"]`) ||
					document.querySelector(`.popup.popup_show`)
				if (popupElement && popupElement.contains(document.activeElement)) {
					document.activeElement.blur()
				}
			})
		})

		popup.visible = false
		updateBodyLock()

		if (popup.closingTimeout) {
			clearTimeout(popup.closingTimeout)
		}
		popup.closingTimeout = setTimeout(() => {
			const index = popups.value.findIndex((p) => p.id === id)
			if (index !== -1) {
				popups.value.splice(index, 1)
			}
		}, 3000)
	}
}

function closeAllPopups() {
	;[...popups.value].forEach((p) => closePopup(p.id))
}

function updateBodyLock() {
	const anyVisible = popups.value.some((p) => p.visible)
	if (anyVisible) {
		document.documentElement.classList.add('popup-show')
		bodyLock()
	} else {
		document.documentElement.classList.remove('popup-show')
		bodyUnlock()
	}
}

// ============== EVENT LISTENERS ==============

function handleGlobalClick(e) {
	const openBtn = e.target.closest('[data-popup]')
	if (openBtn) {
		e.preventDefault()
		const selector = openBtn.getAttribute('data-popup')
		const id = selector.replace('#', '')
		openPopup(id)
	}
}

function handleEsc(e) {
	if (e.key === 'Escape') {
		closeAllPopups()
	}
}

onMounted(() => {
	document.addEventListener('click', handleGlobalClick)
	document.addEventListener('keydown', handleEsc)
})

onUnmounted(() => {
	document.removeEventListener('click', handleGlobalClick)
	document.removeEventListener('keydown', handleEsc)
})

defineExpose({ openPopup, closePopup, closeAllPopups })
</script>

<style scoped lang="scss">
.popup {
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	// padding: 4.5rem 1.5rem;
	padding: 2rem 1.5rem;
	transition: all 0.8s ease 0.1s;
	visibility: hidden;
	pointer-events: none;
	overflow-y: auto;
	overflow-x: hidden;
	z-index: 1000;

	&::after {
		content: '';
		background: rgba($color: #000000, $alpha: 0.5);
		position: fixed;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		opacity: 0;
		transition: opacity 0.8s ease 0s;
		z-index: 1;
	}

	&.popup_show {
		visibility: visible;
		pointer-events: auto;

		&::after {
			opacity: 1;
		}

		.popup__content {
			transform: scale(1);
			// transform: scale(0.8);
			// transform-origin: top ;
		}
	}
}

.popup__wrapper {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 100%;
	flex: 1 1 auto;
	width: 100%;
}

.popup__close {
	position: absolute;
	right: 3.6rem;
	top: 3.6rem;
	z-index: 10;
	width: 3.6rem;
	height: 3.6rem;
	transition: all 0.5s ease 0s;

	@media (max-width: 991.98px) {
		right: 2rem;
		top: 2rem;
		width: 2rem;
		height: 2rem;
	}

	svg,
	img {
		width: 100%;
		height: 100%;
	}

	img {
		object-fit: contain;
	}

	@media (any-hover: hover) {
		&:hover {
			color: var(--primary-color);
			// animation: rotateClose 3s linear infinite;
			transform: rotate(180deg);
		}
	}

	// @keyframes rotateClose {
	// 	0% {
	// 		transform: rotate(0);
	// 	}
	// 	100% {
	// 		transform: rotate(360deg);
	// 	}
	// }
}

.popup__content {
	transform: scale(0);
	transition: all 0.3s ease 0s;
	background-color: #3a3e40;
	// padding: 7.9rem 4.5rem 4.5rem 4.5rem;
	padding: 6rem 4.5rem 4.5rem;
	width: 100%;
	max-width: 76.6rem;
	position: relative;
	z-index: 10;

	@media (max-width: 991.98px) {
		padding: 4rem 2rem;
	}
}
</style>
